<script setup lang="ts">
import { resolveGpoChip } from '~/utils/resolveGpoChip'
import type { GpoChipClinicChain } from '~/utils/resolveGpoChip'

interface Props {
  clinicChain?: GpoChipClinicChain | null
  size?: 'x-small' | 'small' | 'default'
  dataNavId?: string
}

const props = withDefaults(defineProps<Props>(), {
  clinicChain: null,
  size: 'small',
  dataNavId: 'product_clinic_chain',
})

const route = useRoute()
const localePath = useLocalePath()
const chip = computed(() => resolveGpoChip(props.clinicChain))
const chipTo = computed(() =>
  resolvePageSection(route.path) === 'clinic_chain_products'
    ? undefined
    : localePath('/clinic-chain'),
)
</script>

<template>
  <v-tooltip
    v-if="chip.show"
    :disabled="!chip.tooltipKey"
    location="bottom"
    max-width="320"
  >
    <span v-if="chip.tooltipKey">{{ $t(chip.tooltipKey) }}</span>
    <template #activator="{ props: tooltipProps }">
      <v-chip
        v-bind="tooltipProps"
        :to="chipTo"
        :data-nav-id="dataNavId"
        :size="size"
        :tag="chipTo ? 'nuxt-link' : undefined"
        class="mt-1"
        :variant="chip.variant"
        :color="chip.color"
        :class="chip.logoSrc ? ['text-white', 'gpo-buying-list-chip--with-logo'] : undefined"
        data-test-id="gpo-buying-list-chip"
      >
        <template
          v-if="chip.logoSrc"
          #prepend
        >
          <img
            :src="chip.logoSrc"
            :alt="props.clinicChain?.name"
            class="gpo-buying-list-chip__logo"
          >
        </template>
        <span v-if="chip.showName">{{ props.clinicChain?.name }}</span>
        <span
          v-if="chip.labelKey"
          :class="chip.showName ? 'ml-1' : undefined"
        >{{ $t(chip.labelKey) }}</span>
      </v-chip>
    </template>
  </v-tooltip>
</template>

<style scoped lang="scss">
.gpo-buying-list-chip--with-logo {
  padding-inline: 10px;

  :deep(.v-chip__prepend) {
    margin-inline: 0 6px;
  }
}

.gpo-buying-list-chip--with-logo.v-chip--size-x-small {
  padding-inline: 8px;

  :deep(.v-chip__prepend) {
    margin-inline-end: 4px;
  }

  .gpo-buying-list-chip__logo {
    height: 10px;
  }
}

.gpo-buying-list-chip__logo {
  display: block;
  height: 14px;
  width: auto;
  filter: brightness(0) invert(1);
  -webkit-filter: brightness(0) invert(1);
  -moz-filter: brightness(0) invert(1);
  -ms-filter: brightness(0) invert(1);
  -o-filter: brightness(0) invert(1);
}
</style>
